/*
 * Copyright (c) 2018 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Directive, ElementRef, Input, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { ComponentModel } from '@typescript-kit/view';
import { ChangedEvent } from '@typescript-kit/core';

@Directive({
  selector: '[kitClick]'
})
export class ClickDirective implements OnDestroy {

  private readonly element: HTMLElement;
  private unlistenClick: () => void;

  private _model: ComponentModel;
  private _modelChangedSubscription: Subscription;

  constructor(element: ElementRef, private renderer: Renderer2, private ngZone: NgZone) {
    this.element = element.nativeElement;
  }

  ngOnDestroy(): void {
    if (this._modelChangedSubscription) {
      this._modelChangedSubscription.unsubscribe();
      this._modelChangedSubscription = null;
    }

    if (this.unlistenClick) {
      this.unlistenClick();
      this.unlistenClick = null;
    }
  }

  @Input('kitClick')
  set model(model: ComponentModel) {
    if (this.model === model) {
      return;
    }
    if (this._modelChangedSubscription) {
      this._modelChangedSubscription.unsubscribe();
    }
    this._model = model;
    if (model) {
      this._modelChangedSubscription = model.changedEvent
        .subscribe((changedEvent: ChangedEvent) => {
          if (!changedEvent.changes['onClick'] && !changedEvent.changes['isDisabled']) {
            return;
          }
          this.refresh();
        });
    } else {
      this._modelChangedSubscription = null;
    }
    this.refresh();
  }

  get model(): ComponentModel {
    return this._model;
  }

  private refresh() {
    if (this.unlistenClick) {
      this.unlistenClick();
    }
    if (!this.model || !this.model.onClick) {
      return;
    }
    if (!this.model.isDisabled) {
      this.unlistenClick = this.renderer.listen(this.element, 'click', (event: MouseEvent) => {
        this.ngZone.run(() => this.model.onClick(event, this.model));
        event.preventDefault();
      });
    } else {
      this.unlistenClick = null;
    }
  }
}
